import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, ArrowLeft, AlertCircle, X } from 'lucide-react';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import {
  BASE, ICON, fmtINR, fmtNum, num, S, DpoBeamBackground, EMBEDDED_CSS, SectionTitle, Dropdown, ClearFiltersButton,
} from './distributorPoShared';

/* ============================================================================
 * Distributor Master — "SKU Catalog". Same persistent 2/3 (filters + card
 * list) / 1/3 (edit panel) split as GRNManagement.jsx — Edit opens in the
 * sticky right column instead of a modal; "Add New SKU" still uses a modal
 * (creating a SKU isn't tied to an existing row to edit in place) and lives
 * in the top header bar, matching GRN's Upload/Download placement.
 * ========================================================================== */

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const emptySkuForm = {
  id: null, ean: '', internal_sku_code: '', sku_name: '', mrp: '', case_pack: '',
  platform_margin_pct: '', distributor_margin_pct: '', gst_rate: '', remarks: '', is_active: true,
};

// Row-1 column widths shared between the card header and the card's second
// line, so the second line's content starts under SKU Name (not under EAN).
const SR_W = 26, EAN_W = 130, ROW_GAP = 12;

export default function DistributorMasterSkus({ user }) {
  const navigate = useNavigate();
  const canEdit = hasEditAccess(user, 'dms', 'distributor_master');
  const userName = user?.display_name || user?.username || 'Unknown';

  const [distributors, setDistributors] = useState([]);
  const [pickerDistributor, setPickerDistributor] = useState('');
  const [pickerPlatform, setPickerPlatform]       = useState('');
  const [platformOptions, setPlatformOptions]     = useState([]);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [skus, setSkus]             = useState([]);
  const [loading, setLoading]       = useState(false);

  const [addOpen, setAddOpen]   = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [isEdit, setIsEdit]     = useState(false);
  const [form, setForm]         = useState(emptySkuForm);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }

  useEffect(() => {
    fetch(`${BASE}/distributor-master?action=distributors`)
      .then(r => r.json()).then(d => setDistributors(Array.isArray(d) ? d : []))
      .catch(() => setDistributors([]));
  }, []);

  useEffect(() => {
    setPickerPlatform(''); setPlatformOptions([]); setSkus([]);
    if (!pickerDistributor) return;
    fetch(`${BASE}/distributor-master?action=platforms&distributor=${encodeURIComponent(pickerDistributor)}`)
      .then(r => r.json()).then(d => setPlatformOptions(Array.isArray(d) ? d : []))
      .catch(() => setPlatformOptions([]));
  }, [pickerDistributor]);

  const loadSkus = useCallback(async () => {
    if (!pickerDistributor || !pickerPlatform) { setSkus([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ action: 'skus', distributor: pickerDistributor, platform: pickerPlatform });
      if (search)       params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res  = await fetch(`${BASE}/distributor-master?${params}`);
      const data = await res.json();
      setSkus(Array.isArray(data) ? data : []);
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }, [pickerDistributor, pickerPlatform, search, statusFilter]);

  useEffect(() => { loadSkus(); }, [loadSkus]);

  function openNew() {
    if (!canEdit) return notifyEditBlocked();
    if (!pickerDistributor || !pickerPlatform) return showToast('Pick a distributor and platform first');
    setForm(emptySkuForm);
    setIsEdit(false);
    setEditOpen(false);
    setAddOpen(true);
  }
  function openEdit(sku) {
    if (!canEdit) return notifyEditBlocked();
    setForm({
      id: sku.id, ean: sku.ean, internal_sku_code: sku.internal_sku_code || '', sku_name: sku.sku_name || '',
      mrp: sku.mrp ?? '', case_pack: sku.case_pack ?? '', platform_margin_pct: sku.platform_margin_pct ?? '',
      distributor_margin_pct: sku.distributor_margin_pct ?? '', gst_rate: sku.gst_rate ?? '',
      remarks: sku.remarks || '', is_active: !!sku.is_active,
    });
    setIsEdit(true);
    setAddOpen(false);
    setEditOpen(true);
  }
  function closeForm() {
    setAddOpen(false);
    setEditOpen(false);
  }
  async function handleSubmit() {
    if (!form.ean.trim()) return showToast('EAN is required');
    setSubmitting(true);
    try {
      const body = {
        ...form,
        distributor_name: pickerDistributor, platform_name: pickerPlatform,
        mrp: form.mrp === '' ? null : num(form.mrp),
        case_pack: form.case_pack === '' ? null : parseInt(form.case_pack, 10),
        platform_margin_pct: form.platform_margin_pct === '' ? null : num(form.platform_margin_pct),
        distributor_margin_pct: form.distributor_margin_pct === '' ? null : num(form.distributor_margin_pct),
        gst_rate: form.gst_rate === '' ? null : num(form.gst_rate),
        user_name: userName,
      };
      const res  = await fetch(`${BASE}/distributor-master?action=${isEdit ? 'update-sku' : 'create-sku'}`, {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) return showToast(data.error || 'Could not save SKU');
      showToast(data.message);
      closeForm();
      loadSkus();
    } catch (e) { showToast('Network error saving SKU'); }
    setSubmitting(false);
  }

  return (
    <div className="dpo-page" style={S.page}>
      <style>{EMBEDDED_CSS}</style>

      <div style={S.headerBar}>
        <DpoBeamBackground />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          <button style={S.backBtn} onClick={() => navigate('/dms/distributor-master')}><ArrowLeft size={ICON.sm} />Back</button>
          <h1 style={{ ...S.h1 }}>SKU <span style={{ fontWeight: 600, color: '#C9B7EA' }}>Catalog</span></h1>
        </div>
        <button style={{ ...S.btnPrimary, position: 'relative', zIndex: 1 }} onClick={openNew}>
          <Plus size={ICON.sm} style={{ marginRight: 6, verticalAlign: -2 }} />Add New SKU
        </button>
      </div>

      <div style={S.splitBody}>
        <div style={S.splitLeftCol}>
          <div style={S.filterBox}>
            <div style={S.filterField}>
              <div style={S.statLabel}>Distributor *</div>
              <Dropdown value={pickerDistributor} onChange={setPickerDistributor}
                options={distributors.map(d => ({ value: d, label: d }))} placeholder="Pick a distributor" />
            </div>
            <div style={S.filterField}>
              <div style={S.statLabel}>Platform *</div>
              <Dropdown value={pickerPlatform} onChange={setPickerPlatform} disabled={!pickerDistributor}
                options={platformOptions.map(p => ({ value: p, label: p }))}
                placeholder={pickerDistributor ? 'Pick a platform' : 'Pick a distributor first'} />
            </div>
            <div style={S.filterField}>
              <div style={S.statLabel}>Status</div>
              <Dropdown value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
            </div>
            <div style={S.filterField}>
              <div style={S.statLabel}>Search</div>
              <input style={S.input} value={search} placeholder="EAN or SKU name" onChange={e => setSearch(e.target.value)} />
            </div>
            {(search || statusFilter) && (
              <ClearFiltersButton onClear={() => { setSearch(''); setStatusFilter(''); }} />
            )}
          </div>

          <div style={S.card}>
            <SectionTitle n={2} title="SKU Catalog" sub="EAN, pricing, and margins per distributor + platform" />

            {!pickerDistributor || !pickerPlatform ? (
              <div style={S.warnNote}><AlertCircle size={ICON.sm} />Pick a distributor and platform to see its SKU catalog</div>
            ) : loading ? (
              <div style={{ color: 'var(--dpo-text-muted)', padding: '20px 0', textAlign: 'center' }}>Loading…</div>
            ) : skus.length === 0 ? (
              <div style={{ color: 'var(--dpo-text-muted)', padding: '20px 0', textAlign: 'center' }}>No SKUs match these filters</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {skus.map((s, i) => (
                  <div key={s.id} style={S.historyRow}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: ROW_GAP }}>
                      <div style={{ width: SR_W, flexShrink: 0, fontSize: 12.5, color: 'var(--dpo-text-muted)', fontWeight: 600 }}>{i + 1}.</div>
                      <div style={{ width: EAN_W, flexShrink: 0, fontFamily: 'monospace', fontWeight: 700, color: 'var(--dpo-purple)', fontSize: 13.5 }}>{s.ean}</div>
                      <div style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'var(--dpo-text)' }}>{s.sku_name || '—'}</div>
                      <div style={{ fontWeight: 700, color: 'var(--dpo-green)', whiteSpace: 'nowrap' }}>{fmtINR(num(s.mrp))}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: ROW_GAP, marginTop: 8 }}>
                      <div style={{ width: SR_W, flexShrink: 0 }} />
                      <div style={{ width: EAN_W, flexShrink: 0 }} />
                      <div style={{ flex: 1, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--dpo-text-muted)' }}>
                        <span>Case Pack <b style={{ color: 'var(--dpo-text)' }}>{fmtNum(s.case_pack)}</b></span>
                        <span>Platform Margin <b style={{ color: 'var(--dpo-text)' }}>{s.platform_margin_pct != null ? `${s.platform_margin_pct}%` : '—'}</b></span>
                        <span>Distributor Margin <b style={{ color: 'var(--dpo-text)' }}>{s.distributor_margin_pct != null ? `${s.distributor_margin_pct}%` : '—'}</b></span>
                        <span>GST <b style={{ color: 'var(--dpo-text)' }}>{s.gst_rate != null ? `${s.gst_rate}%` : '—'}</b></span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ ...S.statusBadge, background: s.is_active ? 'var(--dpo-green-tint)' : 'var(--dpo-pink-tint)', color: s.is_active ? 'var(--dpo-green)' : 'var(--dpo-error)' }}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button style={S.smallActionBtn} onClick={() => openEdit(s)}><Pencil size={ICON.xs} />Edit</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column — always present, sticky. Placeholder by default;
            swaps to the Edit form in place when a card's Edit is clicked. */}
        <div style={S.splitRightCol}>
          {editOpen ? (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--dpo-text)' }}>Edit SKU</div>
                  <div style={{ fontSize: 11.5, color: 'var(--dpo-text-muted)' }}>for <b>{pickerDistributor}</b> / <b>{pickerPlatform}</b></div>
                </div>
                <button style={S.smallBtn} onClick={closeForm}><X size={ICON.xs} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <FieldBox label="SKU Name" span2 compact>
                  <textarea rows={2} style={{ ...S.input, height: 'auto', minHeight: 40, padding: 6, resize: 'vertical' }}
                    value={form.sku_name} onChange={e => setForm(f => ({ ...f, sku_name: e.target.value }))} />
                </FieldBox>
                <div style={{ ...S.fieldGrid2, gap: 8 }}>
                  <FieldBox label="EAN *" compact><input style={S.input} value={form.ean} disabled onChange={e => setForm(f => ({ ...f, ean: e.target.value }))} /></FieldBox>
                  <FieldBox label="Internal SKU Code" compact><input style={S.input} value={form.internal_sku_code} onChange={e => setForm(f => ({ ...f, internal_sku_code: e.target.value }))} /></FieldBox>
                  <FieldBox label="MRP" compact><input style={S.input} type="number" value={form.mrp} onChange={e => setForm(f => ({ ...f, mrp: e.target.value }))} /></FieldBox>
                  <FieldBox label="Case Pack" compact><input style={S.input} type="number" value={form.case_pack} onChange={e => setForm(f => ({ ...f, case_pack: e.target.value }))} /></FieldBox>
                  <FieldBox label="Distributor Margin %" compact><input style={S.input} type="number" value={form.distributor_margin_pct} onChange={e => setForm(f => ({ ...f, distributor_margin_pct: e.target.value }))} /></FieldBox>
                  <FieldBox label="Platform Margin %" compact><input style={S.input} type="number" value={form.platform_margin_pct} onChange={e => setForm(f => ({ ...f, platform_margin_pct: e.target.value }))} /></FieldBox>
                  <FieldBox label="GST %" compact><input style={S.input} type="number" value={form.gst_rate} onChange={e => setForm(f => ({ ...f, gst_rate: e.target.value }))} /></FieldBox>
                  <FieldBox label="Status" compact>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, cursor: 'pointer', fontSize: 13, color: 'var(--dpo-text)' }}>
                      <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                      {form.is_active ? 'Active' : 'Inactive'}
                    </label>
                  </FieldBox>
                </div>
                <FieldBox label="Remarks" span2 compact>
                  <textarea style={{ ...S.input, height: 40, padding: 6 }} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
                </FieldBox>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                <button style={S.btnGhost} onClick={closeForm}>Cancel</button>
                <button style={{ ...S.btnPrimary, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={handleSubmit}>
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--dpo-text-muted)' }}>
              <Pencil size={ICON.lg} style={{ opacity: 0.35, marginBottom: 10 }} />
              <div style={{ fontSize: 13 }}>Click a SKU's Edit button to view and update its details here.</div>
            </div>
          )}
        </div>
      </div>

      {addOpen && (
        <div style={S.overlay} onClick={closeForm}>
          <div style={{ ...S.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--dpo-text)', marginBottom: 4 }}>Add New SKU</div>
            <div style={{ fontSize: 12.5, color: 'var(--dpo-text-muted)', marginBottom: 18 }}>
              For <b>{pickerDistributor}</b> / <b>{pickerPlatform}</b>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={S.statLabel}>SKU Name</div>
              <input style={{ ...S.input, marginTop: 6 }} value={form.sku_name} onChange={e => setForm(f => ({ ...f, sku_name: e.target.value }))} />
            </div>
            <div style={{ ...S.bentoGrid, marginBottom: 12 }}>
              <FormField label="EAN *"><input style={S.input} value={form.ean} onChange={e => setForm(f => ({ ...f, ean: e.target.value }))} /></FormField>
              <FormField label="Internal SKU Code"><input style={S.input} value={form.internal_sku_code} onChange={e => setForm(f => ({ ...f, internal_sku_code: e.target.value }))} /></FormField>
              <FormField label="MRP"><input style={S.input} type="number" value={form.mrp} onChange={e => setForm(f => ({ ...f, mrp: e.target.value }))} /></FormField>
              <FormField label="Case Pack"><input style={S.input} type="number" value={form.case_pack} onChange={e => setForm(f => ({ ...f, case_pack: e.target.value }))} /></FormField>
            </div>
            <div style={S.bentoGrid}>
              <FormField label="Platform Margin %"><input style={S.input} type="number" value={form.platform_margin_pct} onChange={e => setForm(f => ({ ...f, platform_margin_pct: e.target.value }))} /></FormField>
              <FormField label="Distributor Margin %"><input style={S.input} type="number" value={form.distributor_margin_pct} onChange={e => setForm(f => ({ ...f, distributor_margin_pct: e.target.value }))} /></FormField>
              <FormField label="GST %"><input style={S.input} type="number" value={form.gst_rate} onChange={e => setForm(f => ({ ...f, gst_rate: e.target.value }))} /></FormField>
              <FormField label="Status">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, cursor: 'pointer', fontSize: 13, color: 'var(--dpo-text)' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                  {form.is_active ? 'Active' : 'Inactive'}
                </label>
              </FormField>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={S.statLabel}>Remarks</div>
              <textarea style={{ ...S.input, height: 64, padding: 10, marginTop: 6 }} value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
              <button style={S.btnGhost} onClick={closeForm}>Cancel</button>
              <button style={{ ...S.btnPrimary, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={handleSubmit}>
                {submitting ? 'Saving…' : 'Add SKU'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={S.bentoCard}>
      <div style={S.statLabel}>{label}</div>
      {children}
    </div>
  );
}

// Label on top (solid black, matching the requested edit-panel style),
// input below — two fields per row via the fieldGrid2 wrapper, except
// SKU Name/Remarks which span both columns (span2).
function FieldBox({ label, span2, compact, children }) {
  return (
    <div style={{
      ...S.bentoCard,
      ...(span2 ? { gridColumn: 'span 2' } : {}),
      ...(compact ? { padding: '8px 10px', gap: 4, minHeight: 'auto' } : {}),
    }}>
      <div style={S.fieldBoxLabel}>{label}</div>
      {children}
    </div>
  );
}
